import { createRouter, createWebHistory } from 'vue-router'
import routes from './routes' // Assuming all route definitions are moved here
import store from '@/store'
import cognitoAuth from '@/cognito'
import { authHelpers } from '@/api/authHelpers'
import { embedNavigationTarget } from './embedGuard'
import { isSessionExpiredError } from '@/utils/sessionExpired'
import { withoutLinkAction } from '@/utils/linkActions'
import { loginRoute, safeRedirectPath } from '@/utils/loginRedirect'

const allowedRoutesListWithoutSubscription = ['/plans', '/checkout', '/payment', '/account']

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach(async (to, from, next) => {
  // Keep `?embed=1` in the URL across internal navigations that would otherwise drop it.
  const embedTarget = embedNavigationTarget(to)
  if (embedTarget) {
    next(embedTarget)
    return
  }

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const requiresNoAuth = to.matched.some(record => record.meta.requiresNoAuth)
  const requiresProject = to.matched.some(record => record.meta.requiresProject)
  const allowNonTeamMember = to.matched.some(record => record.meta.allowNonTeamMember)

  try {
    const authenticated = await cognitoAuth.getValidSession()

    if (requiresAuth && (!authenticated || !store.state.isUserLoggedIn)) {
      handleUnauthenticatedUser(to, next)
      return
    }

    if (authenticated) {
      await handleAuthenticatedUser(to, from, next, requiresNoAuth, requiresProject, allowNonTeamMember)
      return
    }

    if (shouldRedirectToWorkflow(to, from)) {
      redirectToWorkflow(to, from, next)
      return
    }

    next()
  } catch (error) {
    console.error('Authentication error', error)

    const sessionEndedDuringUse = isSessionExpiredError(error) && cognitoAuth.hadValidSession()
    if (sessionEndedDuringUse) {
      authHelpers.expireSession(to.fullPath, 'navigation')
      next(loginRoute(to.fullPath))

      return
    }

    if (requiresAuth) {
      handleUnauthenticatedUser(to, next)

      return
    }

    next()
  }
})

function handleUnauthenticatedUser (to, next) {
  authHelpers.logoutAndClearStore()
  next(loginRoute(to.fullPath, { projectId: to.params.projectId }))
}

async function handleAuthenticatedUser (to, from, next, requiresNoAuth, requiresProject, allowNonTeamMember) {
  if (allowNonTeamMember) {
    nextWithQueryParams(to, from, next)
    return
  }

  if (requiresNoAuth) {
    // Cognito keeps its tokens until the logout revoke returns, so trust the store
    const isLogin = to.matched.some(record => record.path === '/login')
    const redirect = isLogin && store.state.isUserLoggedIn ? safeRedirectPath(to.query.redirect) : null
    if (redirect) {
      next(redirect)
    } else {
      next()
    }
    return
  } else {
    if (!haveActiveSubscription(to.fullPath)) {
      next('/plans')
      return
    }
  }

  const validProject = requiresProject ? await projectIsValid(to.params.projectId) : true

  if (!validProject) {
    next('/404')
    return
  }

  nextWithQueryParams(to, from, next)
}

function nextWithQueryParams (to, from, next) {
  if (!hasQueryParams(to) && hasQueryParams(from) && !!from.query.selectedTab && to.path.includes('/workflow/')) {
    // keep selected tab open
    next({
      path: to.path,
      query: withoutLinkAction(from.query)
    })
  } else {
    next()
  }
}

function redirectToWorkflow (to, from, next) {
  next({
    path: to.path,
    params: to.params,
    query: withoutLinkAction(from.query)
  })
}

function shouldRedirectToWorkflow (to, from) {
  return !hasQueryParams(to) && hasQueryParams(from) && from.query.selectedTab && to.path.includes('/workflow/')
}

function hasQueryParams (route) {
  return !!Object.keys(route.query).length
}

async function projectIsValid (projectId) {
  if (store.state.adminUser) {
    return true
  }
  return await checkProjectInMemberships(projectId)
}

async function checkProjectInMemberships (projectId) {
  let listProjectMembershipsForCurrentUser = store.state.projectMemberships.projectMemberships
  if (listProjectMembershipsForCurrentUser?.some(membership => membership.project?.id === projectId)) {
    return true
  }
  await store.dispatch('projectMemberships/listProjectMembershipsForCurrentUser')
  listProjectMembershipsForCurrentUser = store.state.projectMemberships.projectMemberships
  return !!listProjectMembershipsForCurrentUser?.some(membership => membership.project?.id === projectId)
}

function haveActiveSubscription (fullPath) {
  const subscriptionAccessType = store.state.payments.subscriptionAccessType
  const activeSubscription = store.state.payments.activeSubscription
  const loadingActiveSubscription = store.state.payments.loadingActiveSubscription
  const shouldAccessAdmin = store.state.adminUser && fullPath?.startsWith('/admin')

  return !(!loadingActiveSubscription &&
    !!activeSubscription &&
    !allowedRoutesListWithoutSubscription.includes(fullPath) &&
    subscriptionAccessType === 'NO_ACCESS' &&
    !shouldAccessAdmin)
}

export default router
